/**
 * Trovva — Confusion Detection Tracker
 *
 * For the first 10 logins, tracks user navigation patterns to detect
 * confusion. If the pattern shows the user doesn't know where to go,
 * the Ask Trovva button proactively pops a help offer.
 *
 * Signals tracked:
 *   1. Rapid page switches (>3 nav changes in 10 seconds = "ping-ponging")
 *   2. Returning to the same page repeatedly (3+ visits to same page in 60s)
 *   3. Idle time on a page (>45 seconds without interaction = "stuck")
 *   4. Clicking non-interactive areas (clicking on blank spaces)
 *
 * Confusion score: each signal adds points. When score exceeds threshold,
 * triggers a callback (used to pop the Brain chat with a help offer).
 *
 * Usage:
 *   const tracker = createConfusionTracker({ onConfused: () => showHelpBubble() });
 *   tracker.trackNavigation('/crm');      // call on page change
 *   tracker.trackClick(event);             // call on click
 *   tracker.trackInteraction();            // call on meaningful interaction
 *   tracker.reset();                       // reset after help is shown
 *   tracker.destroy();                     // cleanup
 */

const CONFUSION_THRESHOLD = 8;
const RAPID_NAV_WINDOW = 10000;   // 10 seconds
const RAPID_NAV_COUNT = 4;        // 4+ navs in 10s = confused
const REPEAT_VISIT_WINDOW = 60000; // 60 seconds
const REPEAT_VISIT_COUNT = 3;     // 3+ visits to same page in 60s
const IDLE_TIMEOUT = 45000;       // 45 seconds idle = stuck
const COOLDOWN = 120000;          // 2 min cooldown between help offers
const MAX_LOGIN_COUNT = 10;       // stop tracking after 10 logins

export function createConfusionTracker({ onConfused, getLoginCount }) {
  let score = 0;
  let navHistory = [];       // [{ page, time }]
  let lastInteraction = Date.now();
  let idleTimer = null;
  let lastHelpOffer = 0;
  let destroyed = false;

  function shouldTrack() {
    if (destroyed) return false;
    const loginCount = getLoginCount?.() || 0;
    return loginCount <= MAX_LOGIN_COUNT;
  }

  function addScore(points, reason) {
    if (!shouldTrack()) return;
    score += points;
    if (score >= CONFUSION_THRESHOLD && (Date.now() - lastHelpOffer > COOLDOWN)) {
      lastHelpOffer = Date.now();
      score = 0; // reset after triggering
      onConfused?.(reason);
    }
  }

  function trackNavigation(page) {
    if (!shouldTrack()) return;
    const now = Date.now();
    navHistory.push({ page, time: now });

    // Prune old entries
    navHistory = navHistory.filter(n => now - n.time < REPEAT_VISIT_WINDOW);

    // Check rapid navigation (ping-ponging)
    const recentNavs = navHistory.filter(n => now - n.time < RAPID_NAV_WINDOW);
    if (recentNavs.length >= RAPID_NAV_COUNT) {
      addScore(3, 'rapid_navigation');
    }

    // Check repeat visits to same page
    const samePageVisits = navHistory.filter(n => n.page === page);
    if (samePageVisits.length >= REPEAT_VISIT_COUNT) {
      addScore(3, 'repeat_visits');
    }

    // Reset idle timer
    resetIdleTimer();
    lastInteraction = now;
  }

  function trackClick(event) {
    if (!shouldTrack()) return;
    lastInteraction = Date.now();
    resetIdleTimer();

    // Detect clicks on non-interactive elements (body, div, main, section)
    const tag = event?.target?.tagName?.toLowerCase();
    const isInteractive = ['a', 'button', 'input', 'select', 'textarea', 'label'].includes(tag)
      || event?.target?.closest?.('a, button, [role="button"], [onclick]');
    if (!isInteractive) {
      addScore(1, 'non_interactive_click');
    }
  }

  function trackInteraction() {
    if (!shouldTrack()) return;
    lastInteraction = Date.now();
    resetIdleTimer();
    // Meaningful interaction reduces score slightly
    score = Math.max(0, score - 1);
  }

  function resetIdleTimer() {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      if (!shouldTrack()) return;
      addScore(2, 'idle_on_page');
    }, IDLE_TIMEOUT);
  }

  function reset() {
    score = 0;
    navHistory = [];
    if (idleTimer) clearTimeout(idleTimer);
  }

  function destroy() {
    destroyed = true;
    if (idleTimer) clearTimeout(idleTimer);
  }

  // Start idle timer immediately
  resetIdleTimer();

  return { trackNavigation, trackClick, trackInteraction, reset, destroy };
}
